import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Typography,
  Space,
  Button,
  message,
  Spin,
  Empty,
  Alert,
  Card,
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { UsageService, UsageData, UsageHistoryData, UsageWarning } from '../services/usageService';
import FeatureUsageCard from '../components/usage/FeatureUsageCard';
import UsageChart from '../components/usage/UsageChart';
import LimitSettingModal from '../components/usage/LimitSettingModal';
import { getActiveBrandId } from '../config/api';

const { Title, Text } = Typography;

const FeatureUsage: React.FC = () => {
  const { user } = useAuth();
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [historyData, setHistoryData] = useState<UsageHistoryData[]>([]);
  const [warnings, setWarnings] = useState<UsageWarning[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Modal state
  const [limitModalVisible, setLimitModalVisible] = useState(false);
  const [selectedUsage, setSelectedUsage] = useState<UsageData | null>(null);
  
  // Chart filter
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

  const activeBrandId = getActiveBrandId(user);

  const loadUsageData = useCallback(async () => {
    if (!activeBrandId) return;

    try {
      setLoading(true);
      const data = await UsageService.getUsageStats(activeBrandId);
      setUsageData(data);
    } catch (error) {
      console.error('Failed to load usage data:', error);
      message.error('Kullanım verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [activeBrandId]);

  const loadHistoryData = useCallback(async () => {
    if (!activeBrandId) return;

    try {
      setHistoryLoading(true);
      const data = await UsageService.getUsageHistory(activeBrandId, 6);
      setHistoryData(data);
    } catch (error) {
      console.error('Failed to load history data:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [activeBrandId]);

  const loadWarnings = useCallback(async () => {
    if (!activeBrandId) return;

    try {
      const warningData = await UsageService.getWarnings(activeBrandId);
      setWarnings(warningData);
    } catch (error) {
      console.error('Failed to load warnings:', error);
    }
  }, [activeBrandId]);

  useEffect(() => {
    if (user && activeBrandId) {
      loadUsageData();
      loadHistoryData();
      loadWarnings();
    }
  }, [user, activeBrandId, loadUsageData, loadHistoryData, loadWarnings]);

  const handleRefresh = () => {
    loadUsageData();
    loadHistoryData();
    loadWarnings();
  };

  const handleSetLimit = (featureCode: string) => {
    const usage = usageData.find(u => u.featureCode === featureCode);
    if (usage) {
      setSelectedUsage(usage);
      setLimitModalVisible(true);
    }
  };

  const handleSaveLimit = async (limit: number) => {
    if (!activeBrandId || !selectedUsage) return;

    try {
      await UsageService.setCustomLimit(activeBrandId, selectedUsage.featureCode, limit);
      message.success('Limit başarıyla ayarlandı');
      setLimitModalVisible(false);
      setSelectedUsage(null);
      loadUsageData();
    } catch (error) {
      console.error('Failed to set limit:', error);
      message.error('Limit ayarlanamadı');
      throw error;
    }
  };

  const handleExportReport = async () => {
    if (!activeBrandId) return;

    try {
      const currentMonth = new Date().toISOString().split('T')[0].substring(0, 8) + '01';
      const blob = await UsageService.exportUsageReport(activeBrandId, currentMonth);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-report-${currentMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Rapor başarıyla indirildi');
    } catch (error) {
      console.error('Failed to export report:', error);
      message.error('Rapor indirilemedi');
    }
  };

  const handleFeatureToggle = (featureCode: string, checked: boolean) => {
    if (checked) {
      setSelectedFeatures(prev => [...prev, featureCode]);
    } else {
      setSelectedFeatures(prev => prev.filter(f => f !== featureCode));
    }
  };

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!activeBrandId) {
    return (
      <Empty description="Marka bilgisi bulunamadı" />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Feature Kullanım Dashboard</Title>
          <Text type="secondary">
            Aylık feature kullanımlarınızı takip edin ve limitler belirleyin
          </Text>
        </div>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportReport}
          >
            Rapor İndir
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Yenile
          </Button>
        </Space>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert
          message={`${warnings.length} Feature Limit Uyarısı`}
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {warnings.map(warning => (
                <div key={warning.featureCode}>
                  <Text strong>{warning.featureName}:</Text>
                  {' '}
                  <Text type="danger">
                    {warning.usageCount} / {warning.baseQuota} (+{warning.overageCount} aşım)
                  </Text>
                </div>
              ))}
            </Space>
          }
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Usage Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" tip="Kullanım verileri yükleniyor..." />
        </div>
      ) : usageData.length === 0 ? (
        <Card>
          <Empty description="Henüz aktif feature bulunmuyor" />
        </Card>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {usageData.map(usage => (
              <Col key={usage.featureCode} xs={24} sm={12} lg={8} xl={6}>
                <FeatureUsageCard
                  usage={usage}
                  onSetLimit={handleSetLimit}
                />
              </Col>
            ))}
          </Row>

          {/* History Chart */}
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin tip="Geçmiş veriler yükleniyor..." />
            </div>
          ) : (
            <UsageChart
              historyData={historyData}
              selectedFeatures={selectedFeatures.length > 0 ? selectedFeatures : undefined}
              onFeatureToggle={handleFeatureToggle}
            />
          )}
        </>
      )}

      {/* Limit Setting Modal */}
      <LimitSettingModal
        visible={limitModalVisible}
        usage={selectedUsage}
        onSave={handleSaveLimit}
        onCancel={() => {
          setLimitModalVisible(false);
          setSelectedUsage(null);
        }}
      />
    </div>
  );
};

export default FeatureUsage;

